import { useEffect, useState } from "react";
import api, { formatApiError, mediaUrl, apkUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { StatusBadge } from "@/components/StatusBadge";
import { AppIcon } from "@/components/AppIcon";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ShoppingCart, Send, MessageSquare, Store, Star, Download, Eye } from "lucide-react";

export default function Market() {
  const { user } = useAuth();
  const { t, tError } = useLang();
  const [apps, setApps] = useState([]);
  const [contactApp, setContactApp] = useState(null);
  const [detailApp, setDetailApp] = useState(null);

  const load = async () => {
    const { data } = await api.get("/market");
    setApps(data);
  };
  useEffect(() => { load(); }, []);

  const requestPurchase = async (app) => {
    try {
      await api.post(`/apps/${app.id}/purchase-request`);
      toast.success(t("toast.requestSent"), { description: t("toast.requestSentDesc") });
    } catch (err) {
      toast.error(tError(formatApiError(err.response?.data?.detail)) || t("toast.failed"));
    }
  };

  const buyNow = () => {
    toast.info(t("toast.walletDisabled"), { description: t("toast.walletDisabledDesc") });
  };

  const download = (app) => {
    const a = document.createElement("a");
    a.href = apkUrl(app.id);
    a.download = `${app.name}.apk`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success(t("toast.downloadStarted"));
  };

  return (
    <div>
      <div className="mb-8">
        <div className="font-mono text-xs text-primary tracking-[0.3em] mb-2">{t("market.tag")}</div>
        <h1 className="font-display font-extrabold text-3xl sm:text-4xl">{t("market.title")}</h1>
        <p className="text-muted-foreground text-sm mt-2">{t("market.subtitle")}</p>
      </div>

      {apps.length === 0 ? (
        <div className="border border-dashed border-border p-16 text-center grid-texture" data-testid="market-empty">
          <Store className="w-10 h-10 text-primary/50 mx-auto mb-4" />
          <p className="text-muted-foreground font-mono text-sm">{t("market.empty")}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {apps.map((app, idx) => {
            const mine = app.owner_id === user?.id;
            const free = !app.price || app.price <= 0;
            return (
              <motion.div key={app.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.05 }}
                data-testid={`market-card-${app.id}`}
                className="border border-border bg-[#0C0D14] p-6 flex flex-col hover:border-primary/50 hover:-translate-y-1 transition-[transform,border-color] duration-200">
                <div className="flex items-start justify-between gap-3 mb-4">
                  <AppIcon name={app.name} category={app.category} src={mediaUrl(app.icon_id)} />
                  <StatusBadge status={app.status} governance={app.governance_status} />
                </div>
                <h3 className="font-display font-bold text-lg">{app.name}</h3>
                <div className="font-mono text-[11px] text-primary/80 uppercase tracking-wider mt-1">{app.category}</div>

                {app.screenshot_ids?.length > 0 && (
                  <div className="flex gap-2 mt-3 overflow-x-auto pb-1">
                    {app.screenshot_ids.slice(0, 3).map((sid) => (
                      <img key={sid} src={mediaUrl(sid)} alt="" className="h-24 w-auto border border-border shrink-0" />
                    ))}
                  </div>
                )}

                <p className="text-muted-foreground text-sm mt-3 line-clamp-2 min-h-[40px]">
                  {app.description || t("market.noDescription")}
                </p>

                <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
                  <div>
                    <div className="font-mono text-[10px] uppercase text-muted-foreground tracking-widest">{t("common.price")}</div>
                    <div className={`font-mono font-bold text-xl ${free ? "text-[#00FF66]" : "text-primary"}`}>
                      {free ? t("common.free") : `€${app.price}`}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono text-[10px] uppercase text-muted-foreground tracking-widest">{t("common.seller")}</div>
                    <div className="flex items-center gap-1 text-sm justify-end">
                      <Star className="w-3 h-3 text-[#FFD700] fill-[#FFD700]" />
                      <span className="font-mono">{app.owner_name}</span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2 mt-5">
                  {free ? (
                    <Button data-testid={`download-${app.id}`} onClick={() => download(app)}
                      className="rounded-none bg-[#00FF66] text-black font-mono text-xs uppercase tracking-wider hover:bg-[#00FF66]/90 col-span-2">
                      <Download className="w-3.5 h-3.5 mr-1.5" /> {t("market.download")}
                    </Button>
                  ) : (
                    <>
                      <Button data-testid={`buy-now-${app.id}`} onClick={buyNow}
                        className="rounded-none bg-primary text-primary-foreground font-mono text-xs uppercase tracking-wider hover:bg-primary/90 col-span-2">
                        <ShoppingCart className="w-3.5 h-3.5 mr-1.5" /> {t("market.buyNow")}
                      </Button>
                      <Button data-testid={`request-purchase-${app.id}`} variant="outline" disabled={mine} onClick={() => requestPurchase(app)}
                        className="rounded-none border-primary/40 text-primary font-mono text-xs uppercase tracking-wider hover:bg-primary/10 disabled:opacity-40">
                        <Send className="w-3.5 h-3.5 mr-1.5" /> {t("market.request")}
                      </Button>
                    </>
                  )}
                  <Button data-testid={`details-${app.id}`} variant="outline" onClick={() => setDetailApp(app)}
                    className="rounded-none border-border text-muted-foreground font-mono text-xs uppercase tracking-wider hover:border-primary/50 hover:text-foreground">
                    <Eye className="w-3.5 h-3.5 mr-1.5" /> {t("market.details")}
                  </Button>
                  <Button data-testid={`contact-seller-${app.id}`} variant="outline" onClick={() => setContactApp(app)}
                    className="rounded-none border-border text-muted-foreground font-mono text-xs uppercase tracking-wider hover:border-primary/50 hover:text-foreground">
                    <MessageSquare className="w-3.5 h-3.5 mr-1.5" /> {t("market.contact")}
                  </Button>
                </div>
                {mine && <div className="font-mono text-[10px] text-muted-foreground mt-2 text-center">{t("market.yourListing")}</div>}
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Detail dialog */}
      <Dialog open={!!detailApp} onOpenChange={(o) => { if (!o) setDetailApp(null); }}>
        <DialogContent className="rounded-none bg-[#0C0D14] border-primary/40 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="detail-dialog">
          {detailApp && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-4">
                  <AppIcon name={detailApp.name} category={detailApp.category} src={mediaUrl(detailApp.icon_id)} />
                  <div>
                    <DialogTitle className="font-display text-xl">{detailApp.name}</DialogTitle>
                    <div className="font-mono text-[11px] text-primary/80 uppercase tracking-wider mt-1">
                      {detailApp.category} · v{detailApp.version}
                    </div>
                  </div>
                </div>
              </DialogHeader>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <StatusBadge status={detailApp.status} governance={detailApp.governance_status} />
                  <span className={`font-mono font-bold text-lg ${!detailApp.price ? "text-[#00FF66]" : "text-primary"}`}>
                    {!detailApp.price ? t("common.free") : `€${detailApp.price}`}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                  {detailApp.description || t("market.noDescription")}
                </p>
                {detailApp.screenshot_ids?.length > 0 && (
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">{t("market.screenshotsLabel")}</div>
                    <div className="flex gap-3 overflow-x-auto pb-2">
                      {detailApp.screenshot_ids.map((sid) => (
                        <img key={sid} src={mediaUrl(sid)} alt="" className="h-64 w-auto border border-border shrink-0" />
                      ))}
                    </div>
                  </div>
                )}
                {(!detailApp.price || detailApp.price <= 0) && detailApp.owner_id !== user?.id && (
                  <Button data-testid="detail-download" onClick={() => download(detailApp)}
                    className="w-full rounded-none bg-[#00FF66] text-black font-mono uppercase tracking-wider hover:bg-[#00FF66]/90">
                    <Download className="w-4 h-4 mr-2" /> {t("market.download")}
                  </Button>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Contact dialog */}
      <Dialog open={!!contactApp} onOpenChange={(o) => { if (!o) setContactApp(null); }}>
        <DialogContent className="rounded-none bg-[#0C0D14] border-primary/40" data-testid="contact-dialog">
          <DialogHeader><DialogTitle className="font-display">{t("market.contactTitle")}</DialogTitle></DialogHeader>
          {contactApp && (
            <div className="space-y-3">
              <p className="text-muted-foreground text-sm">
                {t("market.contactBody1")} <span className="text-primary font-mono">{contactApp.owner_name}</span> {t("market.contactBody2")}
                <span className="text-foreground"> {contactApp.name}</span>.
              </p>
              <div className="border border-border p-4 font-mono text-xs text-muted-foreground">{t("market.contactNote")}</div>
              <Button onClick={() => { requestPurchase(contactApp); setContactApp(null); }}
                className="w-full rounded-none bg-primary text-primary-foreground font-mono uppercase tracking-wider">
                {t("market.sendInstead")}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
